import React, { useMemo } from 'react';
import { Formik, Form, Field } from 'formik';
import {
  Box,
  Typography,
  Grid,
  IconButton,
  CircularProgress,
} from '@mui/material';
import ToggleSwitch from 'application/components/ToggleSwitch/ToggleSwitch';
import FormSection from 'application/components/FormSection/FormSection';
import { IconMenuItem, IconSelect } from '../styledComponents/IconSelect';
import {
  StyledTextField,
  FieldLabel,
  SectionTitle,
  StyledButton,
  SecondaryButton,
  SwitchLabel,
  SwitchDescription,
  TagInputContainer,
  TagChip,
} from './styledFormComponents';
import * as Yup from 'yup';
import {
  buildInitialValues,
  mergeForNewType,
  sanitizeForType,
  contactFieldKeys,
  typeSelectOptions,
  doorFieldDataOptions,
} from '../utils';
import { v4 as uuidv4 } from 'uuid';
import { GamifyIcon } from 'application/components/GamifyIcon';

interface FlexFieldSettingsFormProps {
  field: any; // includes _localKey
  //   selectedCRM: any;
  onSubmit: (values: any) => void;
  onDelete: () => void;
}

export default function FlexFieldSettingsForm({
  field,
  //   selectedCRM,
  onSubmit,
  onDelete,
}: FlexFieldSettingsFormProps) {
  const initialValues = useMemo(() => buildInitialValues(field ?? {}), [field]);
  const flexFieldSchema = Yup.object({
    label: Yup.string().trim().required('Label is required'),

    type: Yup.string()
      .oneOf([
        'String',
        'Notes',
        'Number',
        'Date',
        'Boolean',
        'Money',
        'Email',
        'Phone',
        'List',
        'Power',
        'Order Lines',
        'ImageList',
        'Read Only',
        'WebView',
        'Contact',
      ])
      .required(),
    heading: Yup.string().trim().required('Heading is required'),

    values: Yup.array()
      .of(
        Yup.object({
          id: Yup.string().trim().required('Each option needs an ID'),
          label: Yup.string().trim().required('Each option needs a label'),
        })
      )
      .when('type', {
        is: 'List',
        then: (schema) => schema.min(1, 'At least one option is required'),
        otherwise: (schema) => schema.notRequired(),
      }),

    minRequired: Yup.number().when('type', {
      is: 'ImageList',
      then: (schema) =>
        schema
          .min(1, 'Minimum required must be at least 1')
          .required('Minimum required is required'),
      otherwise: (schema) => schema.notRequired(),
    }),

    suggestedTags: Yup.array()
      .of(Yup.string().trim())
      .when('type', {
        is: 'ImageList',
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.notRequired(),
      }),
  });
  return (
    <Box>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={(values) => {
          const payload = sanitizeForType(values);
          // preserve the stable local key so parent can upsert
          (payload as any)._localKey = field._localKey;
          onSubmit(payload);
        }}
        validationSchema={flexFieldSchema}
      >
        {({
          values,
          errors,
          //   touched,
          handleChange,
          handleBlur,
          setValues,
          setFieldValue,
          isSubmitting,
        }) => (
          <Form>
            <FormSection
              sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              <SectionTitle>Field content</SectionTitle>

              {/* Label */}
              <Box>
                <FieldLabel>Label *</FieldLabel>
                <Field
                  as={StyledTextField}
                  fullWidth
                  name="label"
                  value={values.label}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.label}
                  helperText={errors.label}
                />
                <Typography
                  sx={(theme) => ({
                    fontSize: 12,
                    color: theme.custom?.textLight,
                    mt: 0.5,
                  })}
                >
                  Visible name in the form.
                </Typography>
              </Box>

              {/* Heading */}
              <Box>
                <FieldLabel>Heading</FieldLabel>
                <Field
                  as={StyledTextField}
                  fullWidth
                  name="heading"
                  value={values.heading}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.heading}
                  helperText={errors.heading}
                />
              </Box>

              {/* Type */}
              <Box>
                <FieldLabel>Field Type *</FieldLabel>
                <IconSelect
                  value={values.type}
                  onChange={(e: any) => {
                    const nextType = e.target.value;
                    setValues((curr: any) => mergeForNewType(curr, nextType));
                  }}
                >
                  {typeSelectOptions.map((option) => (
                    <IconMenuItem key={option.value} value={option.value}>
                      {option.label}
                    </IconMenuItem>
                  ))}
                </IconSelect>
              </Box>

              {/* Defaults for common types */}
              {[
                'String',
                'Notes',
                'Number',
                // 'Date',
                'Boolean',
                'Money',
                // 'Email',
                // 'Phone',
              ].includes(values.type) && (
                <Box sx={{ display: 'grid', gap: 2 }}>
                  <Box>
                    <FieldLabel>Default Value</FieldLabel>
                    {values.type === 'Boolean' ? (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography sx={{ fontSize: 14 }}>Default</Typography>
                        <ToggleSwitch
                          checked={!!values.defaultValue}
                          onChange={(checked) =>
                            setFieldValue('defaultValue', checked)
                          }
                        />
                      </Box>
                    ) : (
                      <Field
                        as={StyledTextField}
                        fullWidth
                        name="defaultValue"
                        value={
                          values.defaultValue ??
                          (['Number', 'Money'].includes(values.type)
                            ? null
                            : '')
                        }
                        onChange={handleChange}
                        onBlur={handleBlur}
                        type={
                          ['Number', 'Money'].includes(values.type)
                            ? 'number'
                            : 'text'
                        }
                        placeholder={
                          values.type === 'Date'
                            ? 'YYYY-MM-DD'
                            : 'Enter default value'
                        }
                      />
                    )}
                  </Box>
                </Box>
              )}
              {values.type === 'ImageList' && (
                <>
                  <Box>
                    <FieldLabel>Suggested Image Count</FieldLabel>
                    <Field
                      as={StyledTextField}
                      fullWidth
                      name="suggestedImageCount"
                      type="number"
                      value={values.suggestedImageCount ?? 1}
                      onChange={(e: any) => {
                        const value = parseInt(e.target.value) || 1;
                        setFieldValue(
                          'suggestedImageCount',
                          Math.max(1, value)
                        );
                      }}
                      onBlur={handleBlur}
                      error={!!errors.suggestedImageCount}
                      helperText={
                        errors.suggestedImageCount ||
                        'Suggested number of images (must be at least 1)'
                      }
                      inputProps={{ min: 1 }}
                    />
                  </Box>
                  {/* Suggested Tags Field */}
                  <Box>
                    <FieldLabel>Suggested Tags</FieldLabel>
                    <TagInputContainer>
                      <StyledTextField
                        fullWidth
                        placeholder="Type a tag and press Enter or comma to add"
                        value={values._tagInput || ''}
                        onChange={(e: any) =>
                          setFieldValue('_tagInput', e.target.value)
                        }
                        onKeyDown={(e: any) => {
                          if (e.key === 'Enter' || e.key === ',') {
                            e.preventDefault();
                            const tag = e.target.value.trim();
                            if (
                              tag &&
                              !(values.suggestedTags || []).includes(tag)
                            ) {
                              const newTags = [
                                ...(values.suggestedTags || []),
                                tag,
                              ];
                              setFieldValue('suggestedTags', newTags);
                              setFieldValue('_tagInput', '');
                            }
                          }
                        }}
                        onBlur={(e: any) => {
                          const tag = e.target.value.trim();
                          if (
                            tag &&
                            !(values.suggestedTags || []).includes(tag)
                          ) {
                            const newTags = [
                              ...(values.suggestedTags || []),
                              tag,
                            ];
                            setFieldValue('suggestedTags', newTags);
                            setFieldValue('_tagInput', '');
                          }
                        }}
                      />
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 1,
                          mt: 1,
                        }}
                      >
                        {(values.suggestedTags || []).map(
                          (tag: string, index: number) => (
                            <TagChip
                              key={index}
                              label={tag}
                              onDelete={() => {
                                const newTags = (
                                  values.suggestedTags || []
                                ).filter((_: any, i: number) => i !== index);
                                setFieldValue('suggestedTags', newTags);
                              }}
                              size="small"
                            />
                          )
                        )}
                      </Box>
                      <Typography
                        sx={(theme) => ({
                          fontSize: 12,
                          color: theme.custom?.textLight,
                          mt: 0.5,
                        })}
                      >
                        Add suggested tags to help users categorize their images
                      </Typography>
                    </TagInputContainer>
                  </Box>
                </>
              )}
              {/* Required + pin mapping (for supported types) */}
              {[
                'String',
                'Notes',
                'Number',
                'Date',
                'Boolean',
                'Money',
                'Email',
                'Phone',
                'List',
                'ImageList',
              ].includes(values.type) && (
                <>
                  <FormSection
                    sx={(theme) => ({
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: theme.custom?.background2,
                    })}
                  >
                    <Box>
                      <SwitchLabel>Required</SwitchLabel>
                      <SwitchDescription>
                        Turn on to make this a required field
                      </SwitchDescription>
                    </Box>
                    <ToggleSwitch
                      checked={!!values.required}
                      onChange={(checked) => setFieldValue('required', checked)}
                    />
                  </FormSection>
                </>
              )}
              {/* Pin Mapping Field */}
              {['String', 'Number', 'Email', 'Phone'].includes(values.type) && (
                <Box sx={{ display: 'grid', gap: 2 }}>
                  <Box>
                    <FieldLabel>Pin Mapping Field</FieldLabel>
                    <IconSelect
                      value={values.pinMappingField}
                      onChange={(e: any) =>
                        setFieldValue('pinMappingField', e.target.value)
                      }
                    >
                      {doorFieldDataOptions.map((option) => (
                        <IconMenuItem key={option.value} value={option.value}>
                          {option.label}
                        </IconMenuItem>
                      ))}
                    </IconSelect>
                    <Typography
                      sx={(theme) => ({
                        fontSize: 12,
                        color: theme.custom?.textLight,
                        mt: 0.5,
                      })}
                    >
                      Map a door data field to this field (optional).
                    </Typography>
                  </Box>
                </Box>
              )}

              {values.type === 'ImageList' && values.required && (
                <FormSection>
                  <SectionTitle>Image List Required Settings</SectionTitle>

                  {/* Min Required Field */}
                  <Box>
                    <FieldLabel>Minimum Required Images *</FieldLabel>
                    <Field
                      as={StyledTextField}
                      fullWidth
                      name="minRequired"
                      type="number"
                      value={values.minRequired ?? 1}
                      onChange={(e: any) => {
                        const value = parseInt(e.target.value) || 1;
                        setFieldValue('minRequired', Math.max(1, value));
                      }}
                      onBlur={handleBlur}
                      error={!!errors.minRequired}
                      helperText={
                        errors.minRequired ||
                        'Minimum number of images required (must be at least 1)'
                      }
                      inputProps={{ min: 1 }}
                    />
                  </Box>
                </FormSection>
              )}
              {/* List editor (label+custom id + default by option id) */}
              {values.type === 'List' && (
                <FormSection>
                  <FieldLabel>Options</FieldLabel>
                  {(() => {
                    const ids = (values.values ?? [])
                      .map((o: any) => o.id)
                      .filter(Boolean);
                    const dupSet = new Set(
                      ids.filter((id: any, i: any) => ids.indexOf(id) !== i)
                    );
                    const hasDupes = dupSet.size > 0;

                    return (
                      <>
                        <Box sx={{ display: 'grid', gap: 1, mt: 1 }}>
                          {(values.values ?? []).map(
                            (opt: any, idx: number) => {
                              const idIsDupe = opt.id && dupSet.has(opt.id);
                              const rowKey = opt._rowKey ?? String(idx);
                              return (
                                <Box
                                  key={rowKey}
                                  sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr auto',
                                    gap: 1,
                                    alignItems: 'start',
                                  }}
                                >
                                  <StyledTextField
                                    value={opt.label}
                                    onChange={(e: any) => {
                                      const arr = [...(values.values ?? [])];
                                      arr[idx] = {
                                        ...arr[idx],
                                        label: e.target.value,
                                      };
                                      setFieldValue('values', arr);
                                    }}
                                    placeholder="Name (label)"
                                  />
                                  <StyledTextField
                                    value={opt.id}
                                    onChange={(e: any) => {
                                      const nextID = e.target.value;
                                      const arr = [...(values.values ?? [])];
                                      arr[idx] = { ...arr[idx], id: nextID };
                                      setFieldValue('values', arr);
                                      if (values.defaultValue === opt.id) {
                                        setFieldValue(
                                          'defaultValue',
                                          nextID || null
                                        );
                                      }
                                    }}
                                    placeholder="Custom ID"
                                    error={Boolean(opt.id) && idIsDupe}
                                    helperText={
                                      Boolean(opt.id) && idIsDupe
                                        ? 'Duplicate ID'
                                        : ' '
                                    }
                                  />
                                  <IconButton
                                    onClick={() => {
                                      const arr = [...(values.values ?? [])];
                                      const removed = arr[idx];
                                      arr.splice(idx, 1);
                                      setFieldValue('values', arr);
                                      if (values.defaultValue === removed?.id) {
                                        setFieldValue('defaultValue', null);
                                      }
                                    }}
                                    aria-label="Remove option"
                                  >
                                    ✕
                                  </IconButton>
                                </Box>
                              );
                            }
                          )}
                          <Box>
                            <SecondaryButton
                              type="button"
                              onClick={() =>
                                setFieldValue('values', [
                                  ...(values.values ?? []),
                                  { _rowKey: uuidv4(), id: '', label: '' },
                                ])
                              }
                            >
                              + Add option
                            </SecondaryButton>
                          </Box>
                        </Box>

                        <Box sx={{ mt: 2 }}>
                          <FieldLabel>Default Value</FieldLabel>
                          <IconSelect
                            value={values.defaultValue ?? ''}
                            onChange={(e: any) =>
                              setFieldValue(
                                'defaultValue',
                                e.target.value || null
                              )
                            }
                          >
                            <IconMenuItem key="__none" value="">
                              None
                            </IconMenuItem>
                            {(values.values ?? []).map(
                              (opt: any, i: number) => (
                                <IconMenuItem
                                  key={opt._rowKey ?? `row-${i}`}
                                  value={opt.id}
                                  disabled={!opt.id}
                                >
                                  {opt.label || '(no name)'}{' '}
                                  {opt.id ? `· ${opt.id}` : ''}
                                </IconMenuItem>
                              )
                            )}
                          </IconSelect>
                          {hasDupes && (
                            <Typography
                              sx={{
                                mt: 0.5,
                                fontSize: 12,
                                color: 'error.main',
                              }}
                            >
                              Duplicate IDs detected. IDs must be unique.
                            </Typography>
                          )}
                        </Box>
                      </>
                    );
                  })()}
                </FormSection>
              )}

              {/* WebView */}
              {values.type === 'WebView' && (
                <FormSection
                  sx={(theme) => ({
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor: theme.custom?.background2,
                  })}
                >
                  <Box>
                    <SwitchLabel>Use Linking</SwitchLabel>
                    <SwitchDescription>
                      Use device’s native link to open the web view
                    </SwitchDescription>
                  </Box>
                  <ToggleSwitch
                    checked={!!values.useLinking}
                    onChange={(checked) => setFieldValue('useLinking', checked)}
                  />
                </FormSection>
              )}

              {/* Contact grid */}
              {values.type === 'Contact' && (
                <Box>
                  <FieldLabel>Contact Fields</FieldLabel>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    {contactFieldKeys.map((key) => (
                      <Grid item xs={6} key={key}>
                        <Box
                          sx={(theme) => ({
                            border: `1px solid ${theme.custom?.stroke}`,
                            borderRadius: '8px',
                            p: 2,
                            backgroundColor: theme.custom?.background2,
                          })}
                        >
                          <Typography
                            sx={(theme) => ({
                              mb: 1,
                              fontWeight: 600,
                              color: theme.custom?.text,
                            })}
                          >
                            {key}
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                              Show
                            </Typography>
                            <ToggleSwitch
                              checked={
                                values.contactFields?.[key]?.show || false
                              }
                              onChange={(checked) => {
                                setFieldValue(
                                  `contactFields.${key}.show`,
                                  checked
                                );
                                if (!checked)
                                  setFieldValue(
                                    `contactFields.${key}.required`,
                                    false
                                  );
                              }}
                            />
                          </Box>
                          <Box
                            sx={{
                              mt: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                              Required
                            </Typography>
                            <ToggleSwitch
                              checked={
                                values.contactFields?.[key]?.required || false
                              }
                              onChange={(checked) =>
                                setFieldValue(
                                  `contactFields.${key}.required`,
                                  checked
                                )
                              }
                              disabled={!values.contactFields?.[key]?.show}
                            />
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* Actions */}
              <Box sx={{ display: 'flex', gap: 2, mt: 2, mb: 4 }}>
                <SecondaryButton
                  startIcon={<GamifyIcon icon="trash" color="inherit" />}
                  sx={(theme) => ({
                    color: '#AE1414',
                    borderColor: '#AE1414',
                    '&:hover': {
                      backgroundColor: theme.custom?.isDark
                        ? 'rgba(174, 20, 20, 0.1)'
                        : 'rgba(174, 20, 20, 0.05)',
                    },
                  })}
                  type="button"
                  //   onClick={onDelete}
                  onClick={onDelete}
                  disabled={isSubmitting}
                >
                  {field.name ? 'Delete Field' : 'Discard Field'}
                </SecondaryButton>
                <StyledButton type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <CircularProgress size={24} sx={{ color: 'white' }} />
                  ) : (
                    <Typography>Save Changes</Typography>
                  )}
                </StyledButton>
              </Box>
            </FormSection>
          </Form>
        )}
      </Formik>
    </Box>
  );
}
